import { useCallback } from 'react'
import useSWRInfinite from 'swr/infinite'
import { z } from 'zod'
import { ESArticle } from '../../elastic_search'
import { fb } from '../../firebase'
import { ArticleSearchEndpointParams } from '../../shared/firebase/functions/search_endpoint'
import { deserializeArray } from '../../utils'

type Params = z.input<typeof ArticleSearchEndpointParams>
type SWRKey = {
  params: Params
  page: number
  size: number
  index: 'articles'
}

const PAGE_SIZE = 20

const fetchArticles = async (key: SWRKey): Promise<ESArticle[]> => {
  const { data } = await fb.call('search')(key)
  if (data.index !== 'articles') throw new Error('Unexpected result.')
  return deserializeArray(data.result, {})
}

export const useESArticles = (
  params: Params | null,
  fallback?: ESArticle[],
  pageSize = PAGE_SIZE
) => {
  const getKey = useCallback(
    (index: number): SWRKey | null => {
      if (params === null) return null
      return {
        params,
        page: index + 1,
        size: pageSize,
        index: 'articles',
      }
    },
    [params]
  )
  const { data, size, setSize, isLoading, isValidating } = useSWRInfinite<
    ESArticle[]
  >(getKey, fetchArticles, { fallbackData: fallback ? [fallback] : undefined })

  const isLoadingMore =
    isLoading || (size > 0 && data && typeof data[size - 1] === 'undefined')
  const isEmpty = data?.[0] !== undefined ? data[0].length === 0 : undefined
  const isReachingEnd =
    data !== undefined
      ? (data.length >= 2 &&
          data[data.length - 2].length < pageSize &&
          data[data.length - 1].length === 0) ||
        (data.length === 1 && data[0].length === 0)
      : undefined

  const isRefreshing = isValidating && data && data.length === size

  const articles: ESArticle[] | undefined =
    data && data.length > 0 ? ([] as ESArticle[]).concat(...data) : undefined

  const loadMore = async () => {
    await setSize(size + 1)
  }

  return {
    articles,
    loadMore,
    isReachingEnd,
    isLoadingMore,
    isEmpty,
    isRefreshing,
  }
}
